import { motion } from "framer-motion";
import type { Theme } from "./Nav";

interface Props {
  theme: Theme;
}

/**
 * 环境光背景。
 * 液态玻璃（深色）：B 站粉为主的流动极光，给玻璃提供可折射的内容。
 * 白色：极淡的冷色光晕。新拟态：均匀平色。
 */
export default function AmbientBackground({ theme }: Props) {
  const dark = theme === "dark";
  const neu = theme === "neu";

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* base */}
      <div
        className="absolute inset-0 transition-[background] duration-700"
        style={{
          background: dark
            ? "radial-gradient(120% 90% at 50% 0%, #150d16 0%, #08060b 55%, #040306 100%)"
            : neu
              ? "#e0e5ec"
              : "radial-gradient(120% 90% at 50% 0%, #ffffff 0%, #f6f7fb 55%, #eef0f7 100%)",
        }}
      />

      {dark ? (
        /* ===== 液态玻璃：B 站粉流动极光 ===== */
        <>
          {/* 主粉光带——缓慢斜向流动 */}
          <motion.div
            className="absolute -top-[30%] left-[-10%] h-[80vw] w-[80vw]"
            style={{
              background:
                "radial-gradient(ellipse 60% 45% at 50% 50%, rgba(251,114,153,0.16) 0%, rgba(251,114,153,0.05) 45%, transparent 70%)",
              filter: "blur(50px)",
            }}
            animate={{ x: ["-6%", "8%", "-6%"], y: ["-4%", "6%", "-4%"], rotate: [0, 8, 0] }}
            transition={{ duration: 38, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* 蓝紫辅光——反向漂移，与粉光交叠出紫调 */}
          <motion.div
            className="absolute top-[10%] right-[-20%] h-[60vw] w-[60vw]"
            style={{
              background:
                "radial-gradient(ellipse 55% 45% at 50% 50%, rgba(125,122,255,0.11) 0%, transparent 68%)",
              filter: "blur(60px)",
            }}
            animate={{ x: ["6%", "-8%", "6%"], y: ["5%", "-5%", "5%"], rotate: [0, -10, 0] }}
            transition={{ duration: 46, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* 底部暖粉回光 */}
          <motion.div
            className="absolute bottom-[-35%] left-[20%] h-[60vw] w-[60vw]"
            style={{
              background:
                "radial-gradient(ellipse 55% 45% at 50% 50%, rgba(251,114,153,0.09) 0%, transparent 68%)",
              filter: "blur(70px)",
            }}
            animate={{ x: ["-5%", "7%", "-5%"], y: ["4%", "-6%", "4%"] }}
            transition={{ duration: 52, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* 一道斜向极光纱幕——最强"液态"感来源 */}
          <motion.div
            className="absolute inset-[-20%]"
            style={{
              background:
                "linear-gradient(115deg, transparent 30%, rgba(251,114,153,0.06) 45%, rgba(125,122,255,0.05) 55%, transparent 70%)",
              filter: "blur(30px)",
            }}
            animate={{ x: ["-8%", "8%", "-8%"], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 34, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* 缓慢呼吸的微光斑 */}
          <motion.div
            className="absolute left-[60%] top-[55%] h-[26vw] w-[26vw] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(251,114,153,0.12) 0%, transparent 65%)",
              filter: "blur(40px)",
            }}
            animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0.9, 0.5] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      ) : neu ? null : (
        /* ===== 白色：极淡冷色光晕 ===== */
        <>
          <motion.div
            className="absolute -top-[20%] left-[8%] h-[55vw] w-[55vw] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(41,151,255,0.10) 0%, transparent 65%)",
              filter: "blur(60px)",
            }}
            animate={{ x: [0, 60, -30, 0], y: [0, 40, 10, 0] }}
            transition={{ duration: 42, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-[30%] right-[-15%] h-[48vw] w-[48vw] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(125,122,255,0.08) 0%, transparent 65%)",
              filter: "blur(70px)",
            }}
            animate={{ x: [0, -50, 20, 0], y: [0, 30, -20, 0] }}
            transition={{ duration: 50, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      )}

      {/* fine noise to keep gradients clean */}
      <div
        className="absolute inset-0 mix-blend-overlay"
        style={{
          opacity: dark ? 0.3 : neu ? 0 : 0.18,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
}
